import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
    collectionGroup, 
    query, 
    where, 
    onSnapshot, 
    doc, 
    updateDoc, 
    serverTimestamp 
} from "firebase/firestore";

// CORRECCIÓN: Rutas relativas de Firebase y useAuth recalibradas con precisión
import { db } from '../../../../firebase/config';
import { useAuth } from '../../../hooks/useAuth'; 

import { useTableState } from '../hooks/useTableState';
import { useDataHydration } from '../hooks/useDataHydration';

/**
 * usePendientesTable - Custom Hook encargado de la lógica y persistencia del tablero.
 */
export const usePendientesTable = () => {
    const { user, profile } = useAuth();
    
    // --- ESTADO DE BASE DE DATOS CRUDA ---
    const [rawPolizas, setRawPolizas] = useState([]);
    const [rawContratos, setRawContratos] = useState([]);
    const [loading, setLoading] = useState(true);

    // --- ESTADOS DE CONTROL DE MODALES ---
    const [activeItem, setActiveItem] = useState(null);
    const [showEditPoliza, setShowEditPoliza] = useState(false);
    const [showEditArt, setShowEditArt] = useState(false);
    const [showDetail, setShowDetail] = useState(false);

    // REFS DE CONCURRENCIA
    const activeItemPathRef = useRef(null);
    const isInitialHydrationDone = useRef(false);

    // --- 1. HOOK DE HIDRATACIÓN EN BACKGROUND ---
    // Unimos los flujos de polizas y contratos crudos en un único pool para hidratar sus clientes en background
    const combinedRaw = useMemo(() => [...rawPolizas, ...rawContratos], [rawPolizas, rawContratos]);
    const { clientsCache } = useDataHydration(combinedRaw);

    // --- 2. LISTENERS DE FIRESTORE EN TIEMPO REAL ---
    useEffect(() => {
        const states = ['PENDIENTE DE EMISIÓN', 'PENDIENTE DE INSPECCIÓN', 'PROPUESTA INGRESADA'];
        const qPol = query(collectionGroup(db, 'polizas'), where('estadoEmision', 'in', states));
        const qArt = query(collectionGroup(db, 'contratos'), where('estadoPoliza', 'in', states));

        const unsubPol = onSnapshot(qPol, (snap) => {
            const pols = snap.docs.map(d => ({
                id: d.id,
                path: d.ref.path,
                clienteId: d.ref.path.split('/')[3],
                ...d.data()
            }));
            setRawPolizas(pols);
            setLoading(false);
        }, (error) => console.error("Error cargando polizas pendientes:", error));

        const unsubArt = onSnapshot(qArt, (snap) => {
            const arts = snap.docs.map(d => ({
                id: d.id,
                path: d.ref.path,
                clienteId: d.ref.path.split('/')[3],
                ...d.data()
            }));
            setRawContratos(arts);
            setLoading(false);
        }, (error) => console.error("Error cargando contratos pendientes:", error));

        return () => {
            unsubPol();
            unsubArt();
        };
    }, []);

    // --- 3. MAPEO E HISTORIAL DE ENTIDADES (WATERFALL FALLBACK) ---
    const itemsTable = useMemo(() => {
        return [...rawPolizas, ...rawContratos].map(item => {
            const c = clientsCache[item.clienteId] || {};
            let nombreFinal = `${item.clienteNombre || ''} ${item.clienteApellido || ''}`.trim();
            if (!nombreFinal) {
                nombreFinal = c.persona === 'JURIDICA' ? (c.nombre || '') : `${c.nombre || ''} ${c.apellido || ''}`.trim();
            }

            return {
                ...item,
                displayNombre: (nombreFinal || 'S/D').toUpperCase(),
                displayDoc: item.clienteCuit || item.clienteDni || c.cuit || c.dni || '-',
                canalNombreUnified: item.canalNombre || item.subcanal || '-',
                type: item.nContrato ? 'ART' : 'PATRIMONIAL'
            };
        });
    }, [rawPolizas, rawContratos, clientsCache]);

    // --- 4. HOOK DE CONTROL SÍNCRONO DE TABLAS ---
    const tableState = useTableState({
        data: itemsTable,
        initialSort: { key: 'fechaCarga', direction: 'descending' },
        initialColumns: {
            fechaCarga: true, displayNombre: true, displayDoc: true, motivo: true,
            estadoEmision: true, nPoliza: true, compania: true, riesgo: true,
            suscriptor: true, canal: true
        },
        recordsPerPage: 100
    });

    // Desbloqueo manual/automático
    const unlockPoliza = useCallback(async (path) => {
        if (!path) return;
        try { 
            await updateDoc(doc(db, path), { bloqueo: null }); 
            if (activeItemPathRef.current === path) activeItemPathRef.current = null;
        } catch (e) { console.error("Error al liberar candado:", e); }
    }, []);

    // --- 5. INTERCEPTOR DE ACCIONES ---
    const handleAction = useCallback(async (item, type) => {
        setActiveItem(item);
        if (type === 'view') { 
            setShowDetail(true); 
            return; 
        }
        
        if (type === 'edit') {
            activeItemPathRef.current = item.path;
            if (item.type === 'ART') setShowEditArt(true);
            else setShowEditPoliza(true);

            await updateDoc(doc(db, item.path), { 
                bloqueo: { 
                    userId: user?.uid || 'sys', 
                    userName: profile?.userName || 'Usuario',
                    timestamp: serverTimestamp() 
                } 
            }).catch(e => {
                console.error("Error al bloquear:", e);
                activeItemPathRef.current = null;
            });
        }
    }, [user, profile]);

    return {
        loading,
        clientsCache,
        activeItem,
        showEditPoliza,
        showEditArt,
        showDetail,
        tableState,
        itemsTable,
        activeItemPathRef,
        setShowEditPoliza,
        setShowEditArt,
        setShowDetail,
        setActiveItem,
        handleAction,
        unlockPoliza
    };
};